import { useCallback, useMemo } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager } from "@salestrak/ui";

import {
  ArchiveIssueDocument,
  BulkAddIssueAssigneesDocument,
  BulkMoveIssuesToStageDocument,
  BulkRemoveIssueAssigneesDocument,
  BulkSetPrimaryAssigneeDocument,
  DeleteIssueDocument,
  type EntityType,
  IssueListDataDocument,
  IssueListGroupCountsDocument,
  IssuesDocument,
  MoveIssueToStageDocument,
  UpdateIssueAssigneesDocument,
} from "#app/lib/data";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import { removeFromConnection } from "#app/lib/data/connection-cache-utils";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useIssueListMutations() {
  const [archiveMutation] = useMutation(ArchiveIssueDocument);
  const [changeStageMutation] = useMutation(MoveIssueToStageDocument);
  const [updateAssigneesMutation] = useMutation(UpdateIssueAssigneesDocument);
  const [deleteMutation] = useMutation(DeleteIssueDocument);
  const [bulkMoveMutation] = useMutation(BulkMoveIssuesToStageDocument);
  const issueRefetchQueries = { refetchQueries: [IssueListDataDocument, IssuesDocument, IssueListGroupCountsDocument] };
  const [bulkAssigneesMutation] = useMutation(BulkAddIssueAssigneesDocument, issueRefetchQueries);
  const [bulkRemoveAssigneesMutation] = useMutation(BulkRemoveIssueAssigneesDocument, issueRefetchQueries);
  const [setPrimaryAssigneeMutation] = useMutation(BulkSetPrimaryAssigneeDocument, issueRefetchQueries);

  const archiveIssue = useCallback(
    async (issueId: string) => {
      try {
        await archiveMutation({
          variables: { issueId },
          update(cache, { data }) {
            const result = data?.archiveIssue;
            if (result?.__typename === "Issue") {
              removeFromConnection(cache, "issues", {
                __typename: "Issue",
                issueId: result.issueId,
              });
            }
          },
        });
      } catch (err) {
        toastMutationError(err, "Failed to archive project");
      }
    },
    [archiveMutation],
  );

  const changeStage = useCallback(
    async (issueId: string, stageId: string) => {
      try {
        await changeStageMutation({
          variables: {
            issueId,
            stageId,
            sortOrder: Number.MAX_SAFE_INTEGER,
          },
        });
        toastManager.add({ title: "Stage updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update stage");
      }
    },
    [changeStageMutation],
  );

  const deleteIssue = useCallback(
    async (issueId: string) => {
      try {
        await deleteMutation({
          variables: { issueId },
          update(cache, { data }) {
            const result = data?.deleteIssue;
            if (result?.__typename === "Issue") {
              removeFromConnection(
                cache,
                "issues",
                { __typename: "Issue", issueId: result.issueId },
                { evict: true },
              );
            }
          },
        });
        toastManager.add({ title: "Project deleted", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to delete project");
      }
    },
    [deleteMutation],
  );

  const updateAssignees = useCallback(
    async (issueId: string, entityIds: string[]) => {
      try {
        await updateAssigneesMutation({
          variables: {
            issueId,
            assignees: entityIds.map((id, index) => ({
              entityType: "user" as EntityType,
              entityId: id,
              isPrimary: index === 0,
            })),
          },
        });
        toastManager.add({ title: "Assignees updated", type: "success" });
      } catch (err) {
        toastMutationError(err, "Failed to update assignees");
      }
    },
    [updateAssigneesMutation],
  );

  // ---------------------------------------------------------------------------
  // Dedicated bulk mutations — single GraphQL call per operation.
  // ---------------------------------------------------------------------------

  const bulk = useMemo(
    () => ({
      changeStage: async (issueIds: string[], stageId: string) => {
        await bulkMoveMutation({ variables: { issueIds, stageId } });
      },
      changeStageByWhere: async (where: Record<string, unknown>, stageId: string) => {
        await bulkMoveMutation({ variables: { where, stageId } });
      },
      addAssignees: async (issueIds: string[], entityIds: string[]) => {
        await bulkAssigneesMutation({
          variables: {
            issueIds,
            assignees: entityIds.map((id) => ({
              entityType: "user" as EntityType,
              entityId: id,
            })),
          },
        });
      },
      addAssigneesByWhere: async (where: Record<string, unknown>, entityIds: string[]) => {
        await bulkAssigneesMutation({
          variables: {
            where,
            assignees: entityIds.map((id) => ({
              entityType: "user" as EntityType,
              entityId: id,
            })),
          },
        });
      },
      replaceAssignees: async (issueIds: string[], entityIds: string[]) => {
        await Promise.all(
          issueIds.map((issueId) =>
            updateAssigneesMutation({
              variables: {
                issueId,
                assignees: entityIds.map((id, index) => ({
                  entityType: "user" as EntityType,
                  entityId: id,
                  isPrimary: index === 0,
                })),
              },
            }),
          ),
        );
      },
      removeAssignees: async (issueIds: string[], entityIds: string[]) => {
        await bulkRemoveAssigneesMutation({ variables: { issueIds, entityIds } });
      },
      removeAssigneesByWhere: async (where: Record<string, unknown>, entityIds: string[]) => {
        await bulkRemoveAssigneesMutation({ variables: { where, entityIds } });
      },
      setPrimaryAssignee: async (issueIds: string[], entityId: string) => {
        await setPrimaryAssigneeMutation({ variables: { issueIds, entityId } });
      },
      setPrimaryAssigneeByWhere: async (where: Record<string, unknown>, entityId: string) => {
        await setPrimaryAssigneeMutation({ variables: { where, entityId } });
      },
    }),
    [bulkMoveMutation, bulkAssigneesMutation, updateAssigneesMutation, bulkRemoveAssigneesMutation, setPrimaryAssigneeMutation],
  );

  return { archiveIssue, changeStage, deleteIssue, updateAssignees, bulk };
}
